import { float32ToInt24, sha256Hex } from '@ohear/audio'
import { secondsToSmpteDisplay } from '@ohear/shared'
import { Button, Drawer, ProgressBar } from '@ohear/ui'
/**
 * Top-level recording-page composition — Pro Tools light layout.
 *
 *   ┌────────┬──────────────────────────────────────────────┬──────────┐
 *   │  REF   │ [F] header bar (LCD time + cue # + settings) │  cue list│
 *   │ fader  │ [E] video preview (16:9)                     │  panel   │
 *   │  +     │ [D] WAVEFORM (white track) — focal           │          │
 *   │  meters│ [R] take history strip                       │          │
 *   │        │ [B] coaching banner                          │          │
 *   │        │     CURRENT LINE                             │          │
 *   │        │     [ Big REC button ]   secondary actions   │          │
 *   │        │     progress                                 │          │
 *   └────────┴──────────────────────────────────────────────┴──────────┘
 *
 * All chrome lives in /apps/app/src/features/recording/recorder.css and the
 * `pt.*` palette in @ohear/ui/tailwind-preset. No dark theme remnants — the
 * surface is intentionally fully light so it reads like a real DAW window.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type ActorCue, useBatchCues } from '../../lib/api/cues'
import { useFinalizeTake, usePresignTake } from '../../lib/api/takes'
import { useNetwork } from '../../lib/use-network'
import { BigRecordButton } from './BigRecordButton'
import { BlockModeButtons } from './BlockModeButtons'
import { FrameCommentsOverlay } from './FrameCommentsOverlay'
import { KeyboardShortcuts } from './KeyboardShortcuts'
import { MiniMixer } from './MiniMixer'
import { PredictiveHint } from './PredictiveHint'
import './recorder.css'
import { RefVoiceFader } from './RefVoiceFader'
import { ResetReminder } from './ResetReminder'
import { ScriptPanel } from './ScriptPanel'
import { SecondaryButtons } from './SecondaryButtons'
import { SupervisorBanner } from './SupervisorBanner'
import { TakeHistory } from './TakeHistory'
import { ThreeTakePrompt } from './ThreeTakePrompt'
import { UploadTakeButton } from './UploadTakeButton'
import { VariancePrompt } from './VariancePrompt'
import { WaveformTimeline } from './WaveformTimeline'
import { useRecordingStore } from './state'
import { useRecorder } from './useRecorder'
import { type MachineCue, useRecordingMachine } from './useRecordingMachine'
import { useRefVoiceGain } from './useRefVoiceGain'

/** Default length-variance threshold (±15%). Will read from project QC config later. */
const DEFAULT_LENGTH_VARIANCE_THRESHOLD = 0.15

export interface RecorderShellProps {
  projectId: string
  batchId: string
  /** Hint copy used in the secondary tabs. */
  characterCode: string
  episodeTitle: string
  batchNumber: number
  totalBatches: number
  /** Frame rate from project metadata. */
  frameRate: { fps: number; drop_frame: boolean }
  locale?: 'zh' | 'en'
}

export function RecorderShell({
  projectId,
  batchId,
  characterCode,
  episodeTitle,
  batchNumber,
  totalBatches,
  frameRate,
  locale = 'en',
}: RecorderShellProps) {
  const { data, isLoading, isError, error, refetch } = useBatchCues(projectId, batchId)
  const network = useNetwork()
  const recorder = useRecorder({ sampleRate: 48000 })
  const presign = usePresignTake()
  const finalize = useFinalizeTake()

  const cues = data?.cues ?? []
  const myCues = useMemo(() => cues.filter((c) => c.is_mine), [cues])

  const phase = useRecordingStore((s) => s.phase)
  const currentCueId = useRecordingStore((s) => s.currentCueId)
  const setCurrentCue = useRecordingStore((s) => s.setCurrentCue)
  const setPlayhead = useRecordingStore((s) => s.setPlayhead)
  const setPredictiveCategory = useRecordingStore((s) => s.setPredictiveCategory)
  const predictiveCategory = useRecordingStore((s) => s.predictiveCategoryShown)
  const resetReminderText = useRecordingStore((s) => s.resetReminderText)
  const setResetReminder = useRecordingStore((s) => s.setResetReminder)
  const settingsDrawerOpen = useRecordingStore((s) => s.settingsDrawerOpen)
  const toggleSettingsDrawer = useRecordingStore((s) => s.toggleSettingsDrawer)
  const blockMode = useRecordingStore((s) => s.blockMode)
  const setBlockMode = useRecordingStore((s) => s.setBlockMode)
  const lastError = useRecordingStore((s) => s.lastError)
  const setError = useRecordingStore((s) => s.setError)

  const activeCue: ActorCue | null = useMemo(() => {
    if (currentCueId) return cues.find((c) => c.cue_id === currentCueId) ?? null
    return myCues.find((c) => c.latest_take_status !== 'approved') ?? myCues[0] ?? null
  }, [currentCueId, cues, myCues])

  useEffect(() => {
    if (activeCue && activeCue.cue_id !== currentCueId) {
      setCurrentCue(activeCue.cue_id)
    }
  }, [activeCue, currentCueId, setCurrentCue])

  useEffect(() => {
    setPredictiveCategory(activeCue ? activeCue.category : null)
  }, [activeCue, setPredictiveCategory])

  const previousCategoryRef = useRef<ActorCue['category'] | null>(null)

  const handleAdvance = useCallback(() => {
    if (!activeCue) return
    previousCategoryRef.current = activeCue.category
    const idx = myCues.findIndex((c) => c.cue_id === activeCue.cue_id)
    const next = idx >= 0 && idx + 1 < myCues.length ? myCues[idx + 1] : null
    if (next) setCurrentCue(next.cue_id)
  }, [activeCue, myCues, setCurrentCue])

  const presignFn = useCallback(
    async (input: {
      cueId: string
      takeNumber: number
      versionNumber: number
      durationSec: number
    }) => {
      const placeholderHash = '0'.repeat(64)
      const result = await presign.mutateAsync({
        cueId: input.cueId,
        takeNumber: input.takeNumber,
        versionNumber: input.versionNumber,
        durationSec: input.durationSec,
        sha256: placeholderHash,
      })
      return { takeId: result.take_id, uploadUrl: result.presigned_put_url }
    },
    [presign],
  )

  const uploadFn = useCallback(
    async (input: { takeId: string; uploadUrl: string; pcm: Float32Array }) => {
      const int24 = float32ToInt24(input.pcm)
      const sha256 = await sha256Hex(int24)
      const res = await fetch(input.uploadUrl, {
        method: 'PUT',
        body: int24,
        headers: { 'Content-Type': 'application/octet-stream' },
      })
      if (!res.ok) {
        throw new Error(`Upload failed: ${res.status} ${res.statusText}`)
      }
      return sha256
    },
    [],
  )

  const finalizeFn = useCallback(
    async (input: { takeId: string; cueId: string }) => {
      await finalize.mutateAsync(input)
    },
    [finalize],
  )

  const machineCue: MachineCue | null = activeCue
    ? {
        cueId: activeCue.cue_id,
        durationSec: activeCue.out_sec - activeCue.in_sec,
        category: activeCue.category,
      }
    : null

  const machine = useRecordingMachine({
    recorder,
    callbacks: { presign: presignFn, upload: uploadFn, finalize: finalizeFn },
    activeCue: machineCue,
    previousCategory: previousCategoryRef.current,
    onAdvance: handleAdvance,
  })

  const onPreview = useCallback(() => {
    // Real impl will play activeCue.ref_voice_url; stubbed here.
  }, [])
  const onPlayLast = useCallback(() => {
    // Real impl plays the most recent take (server preview_url or local Blob).
  }, [])
  const onAcceptBlock = useCallback(() => setBlockMode('auto'), [setBlockMode])
  const onSceneBlock = useCallback(() => setBlockMode('scene'), [setBlockMode])
  const onEffortBlock = useCallback(() => setBlockMode('effort'), [setBlockMode])
  const onCustomRange = useCallback(() => setBlockMode('range'), [setBlockMode])

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const videoContainerRef = useRef<HTMLDivElement | null>(null)

  // Reference-voice volume goes through WebAudio so the fader actually
  // attenuates playback level. While the actor is in three_pop / recording
  // we force-mute so the reference doesn't bleed into the mic take.
  useRefVoiceGain({
    videoRef,
    muteForRecord: phase === 'three_pop' || phase === 'recording',
  })

  // Drive the waveform playhead from the reference video's currentTime when
  // the actor is previewing/scrubbing the cue. The video's audio playback is
  // owned by the WebAudio graph (see RefVoiceFader wiring) so we can rely on
  // <video>.timeupdate firing throughout playback.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    const onTimeUpdate = () => setPlayhead(v.currentTime)
    const onSeek = () => setPlayhead(v.currentTime)
    v.addEventListener('timeupdate', onTimeUpdate)
    v.addEventListener('seeked', onSeek)
    return () => {
      v.removeEventListener('timeupdate', onTimeUpdate)
      v.removeEventListener('seeked', onSeek)
    }
  }, [setPlayhead])

  // While we're in the three-pop / recording phase the actor is performing
  // against the cue clock (no <video> playback driving timeupdate) — drive
  // the playhead from a wall-clock RAF so the chase head sweeps across the
  // waveform in lock-step with the take. The clock starts when we first enter
  // the three-pop phase and continues through into recording so the chase
  // head is monotonic across the whole performance window. We reset only
  // when we leave the live-record window (idle / qc_local / upload / review).
  const playheadStartRef = useRef<number | null>(null)
  useEffect(() => {
    const live = phase === 'three_pop' || phase === 'recording'
    if (!live) {
      // Reset between takes so the next take's chase head starts at the head.
      playheadStartRef.current = null
      setPlayhead(0)
      return
    }
    if (playheadStartRef.current === null) {
      playheadStartRef.current = performance.now()
    }
    let raf = 0
    const tick = () => {
      const start = playheadStartRef.current
      if (start === null) return
      const elapsed = (performance.now() - start) / 1000
      setPlayhead(elapsed)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [phase, setPlayhead])

  const cueIn = activeCue
    ? secondsToSmpteDisplay(activeCue.in_sec, {
        fps: frameRate.fps,
        dropFrame: frameRate.drop_frame,
      })
    : '00:00:00:00'
  const cueOut = activeCue
    ? secondsToSmpteDisplay(activeCue.out_sec, {
        fps: frameRate.fps,
        dropFrame: frameRate.drop_frame,
      })
    : '00:00:00:00'

  const approvedCount = myCues.filter((c) => c.latest_take_status === 'approved').length
  const progressPct = myCues.length > 0 ? (approvedCount / myCues.length) * 100 : 0

  const pickupQueue = useMemo(() => new Set(data?.pickup_queue ?? []), [data?.pickup_queue])

  const activeCueId = activeCue?.cue_id ?? null
  const hasLocalTake = useRecordingStore((s) =>
    activeCueId ? (s.takesByCue[activeCueId]?.length ?? 0) > 0 : false,
  )

  const localTakeCount = useRecordingStore((s) =>
    activeCueId ? (s.takesByCue[activeCueId]?.length ?? 0) : 0,
  )

  const lastTakeDurationSec = useRecordingStore((s) => {
    if (!activeCueId) return null
    const list = s.takesByCue[activeCueId]
    if (!list || list.length === 0) return null
    return list[list.length - 1]?.durationSec ?? null
  })
  const refDurationSec = activeCue ? activeCue.out_sec - activeCue.in_sec : 0
  const lengthVariance = useMemo(() => {
    if (lastTakeDurationSec == null || refDurationSec <= 0) return 0
    return (lastTakeDurationSec - refDurationSec) / refDurationSec
  }, [lastTakeDurationSec, refDurationSec])

  const [variancePromptDismissedFor, setVariancePromptDismissedFor] = useState<{
    cueId: string
    takeCount: number
  } | null>(null)
  const variancePromptOpen =
    phase === 'review' &&
    Math.abs(lengthVariance) > DEFAULT_LENGTH_VARIANCE_THRESHOLD &&
    !!activeCueId &&
    !(
      variancePromptDismissedFor &&
      variancePromptDismissedFor.cueId === activeCueId &&
      variancePromptDismissedFor.takeCount === localTakeCount
    )

  const dismissVariancePrompt = useCallback(() => {
    if (!activeCueId) return
    setVariancePromptDismissedFor({ cueId: activeCueId, takeCount: localTakeCount })
  }, [activeCueId, localTakeCount])

  /**
   * Three-take soft nudge — fires when the actor presses N (advance) but only
   * has 1 or 2 takes recorded for the active cue. ADR-industry convention is
   * 3 takes per line; this is a one-time gentle reminder per cue, never a
   * blocker. At 3+ takes the prompt is suppressed (they hit the convention
   * already, no need to nag). State shape:
   *   - `cueId` we already nagged the actor about
   *   - `nudgeRequested` flag — set by the wrapped `requestAdvance` callback
   *     when the actor tries to skip ahead with <3 takes
   * Once dismissed for a cue, subsequent N presses pass straight through.
   */
  const [threeTakePromptDismissedFor, setThreeTakePromptDismissedFor] = useState<string | null>(
    null,
  )
  const [nudgeRequested, setNudgeRequested] = useState<string | null>(null)
  const threeTakePromptOpen =
    !!activeCueId &&
    nudgeRequested === activeCueId &&
    threeTakePromptDismissedFor !== activeCueId &&
    !variancePromptOpen

  const dismissThreeTakePrompt = useCallback(() => {
    if (!activeCueId) return
    setThreeTakePromptDismissedFor(activeCueId)
    setNudgeRequested(null)
  }, [activeCueId])

  /**
   * Wrapped advance. When the actor presses N with only 1 or 2 takes on the
   * active cue (and we haven't already nudged them once for this cue), open
   * the ThreeTakePrompt instead of advancing. The prompt's actions handle
   * the actual advance / re-record / dismiss decision.
   */
  const requestAdvance = useCallback(() => {
    if (
      phase === 'review' &&
      activeCueId &&
      localTakeCount >= 1 &&
      localTakeCount < 3 &&
      threeTakePromptDismissedFor !== activeCueId
    ) {
      setNudgeRequested(activeCueId)
      return
    }
    machine.advance()
  }, [phase, activeCueId, localTakeCount, threeTakePromptDismissedFor, machine])

  // Test-only state exposure — lets Playwright specs read the recording store
  // without importing React internals. Gated on __OHEAR_TEST_MODE__ so
  // production bundles never expose the snapshot.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.__OHEAR_TEST_MODE__ !== true) return
    window.__OHEAR_RECORDING_STATE__ = () => {
      const s = useRecordingStore.getState()
      return {
        phase: s.phase,
        playheadSec: s.playheadSec,
        currentCueId: s.currentCueId,
        takesByCue: Object.fromEntries(
          Object.entries(s.takesByCue).map(([cueId, takes]) => [
            cueId,
            takes.map((t) => ({
              takeId: t.takeId,
              takeNumber: t.takeNumber,
              versionNumber: t.versionNumber,
              durationSec: t.durationSec,
              uploadStatus: t.uploadStatus,
            })),
          ]),
        ),
      }
    }
    return () => {
      if (window.__OHEAR_RECORDING_STATE__) {
        window.__OHEAR_RECORDING_STATE__ = undefined
      }
    }
  }, [])

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center bg-pt-bg text-pt-text-soft">
        <p className="pt-mono text-[11px] uppercase tracking-[0.18em]">Loading cues…</p>
      </div>
    )
  }
  if (isError) {
    return (
      <div className="bg-pt-bg p-6">
        <p className="mb-2 pt-ribbon inline-block px-3 py-1.5 text-[13px]">
          Could not load cues: {error instanceof Error ? error.message : 'unknown error'}
        </p>
        <div className="mt-3">
          <Button variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <KeyboardShortcuts
        onPreview={onPreview}
        onBeginRecord={() => {
          if (!network.online) {
            setError('You are offline. Connect to record new takes.')
            return
          }
          void machine.beginRecord()
        }}
        onPickup={() => void machine.pickup()}
        onAdvance={requestAdvance}
        onAbort={machine.abort}
        onAcceptBlock={onAcceptBlock}
        onSceneBlock={onSceneBlock}
        onEffortBlock={onEffortBlock}
        onPlayLast={onPlayLast}
        locale={locale}
      />

      {/* PT-light surface: classic light-grey console background. */}
      <div
        className="grid grid-cols-1 bg-pt-bg text-pt-text lg:grid-cols-[88px_1fr_minmax(360px,_400px)]"
        style={{ height: 'calc(100vh - 100px)' }}
      >
        {/* LEFT EDGE — vertical aluminum reference fader (lg+ only). */}
        <aside className="hidden h-full border-r border-pt-border bg-pt-chrome p-2 lg:flex lg:flex-col">
          <RefVoiceFader orientation="vertical" />
        </aside>

        {/* CENTER — header, video, waveform, controls. */}
        <main className="flex min-w-0 flex-col gap-2 px-3 py-3">
          {/* Top header strip — PT-style title bar with LCD time + meta. */}
          <header className="flex items-center justify-between rounded-sm border border-pt-border bg-pt-chrome px-3 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]">
            <div className="flex items-center gap-2 pt-mono text-[11px]">
              <span className="pt-lcd pt-lcd--green">{cueIn}</span>
              <span className="text-pt-text-soft">→</span>
              <span className="pt-lcd pt-lcd--green">{cueOut}</span>
              <span className="ml-2 rounded-sm border border-pt-border bg-pt-panel px-1.5 py-0.5 uppercase text-pt-text">
                {characterCode}
              </span>
              <span className="text-pt-text-soft">·</span>
              <span className="text-pt-text">
                {episodeTitle} · Batch <span className="font-bold">{batchNumber}</span>
                <span className="text-pt-text-soft">/{totalBatches}</span>
              </span>
              {!network.online && (
                <span className="ml-2 inline-flex items-center gap-1.5 rounded-sm border border-pt-text bg-pt-warn px-2 py-0.5 text-pt-text">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-pt-rec" />
                  OFFLINE
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="pt-lcd pt-lcd--amber text-[11px]" title="Cue number">
                CUE {activeCue?.cue_number?.toString().padStart(3, '0') ?? '---'}
              </span>
              <button
                type="button"
                onClick={toggleSettingsDrawer}
                className="pt-btn px-2.5 py-1 text-[11px]"
              >
                ⚙ SETTINGS
              </button>
            </div>
          </header>

          {/* Video preview + frame comments — PT panel frame. */}
          <div
            ref={videoContainerRef}
            className="relative overflow-hidden rounded-sm border border-pt-border bg-pt-lcd-bg shadow-[inset_0_1px_2px_rgba(0,0,0,0.6)]"
            style={{ aspectRatio: '16 / 9', maxHeight: '32vh' }}
          >
            <video
              ref={videoRef}
              className="absolute inset-0 h-full w-full"
              playsInline
              muted
              aria-label="Reference video for this cue"
            />
            <div className="absolute inset-0 flex items-center justify-center text-center text-pt-text-soft">
              <div>
                <div className="mb-1 text-3xl opacity-50">🎬</div>
                <div className="pt-mono text-[10px] uppercase tracking-[0.18em] text-pt-lcd-amber">
                  {activeCue
                    ? `Cue #${activeCue.cue_number} · ${(activeCue.out_sec - activeCue.in_sec).toFixed(2)}s`
                    : 'No cue selected'}
                </div>
              </div>
            </div>
            <FrameCommentsOverlay
              projectId={projectId}
              videoRef={videoRef}
              containerRef={videoContainerRef}
              activeCueId={activeCue?.cue_id ?? null}
              activeCueInSec={activeCue?.in_sec ?? null}
            />
          </div>

          {/* WAVEFORM panel — white track with PT chrome frame. */}
          <section
            className="flex min-h-[200px] flex-1 flex-col rounded-sm border border-pt-border bg-pt-panel shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
            aria-label="Waveform timeline"
          >
            <div className="flex items-center justify-between border-b border-pt-border bg-pt-chrome px-3 py-1.5 pt-mono text-[10px] uppercase tracking-[0.16em] text-pt-text">
              <div className="flex items-center gap-3">
                <span className="font-bold">WAVEFORM</span>
                <span className="text-pt-text-soft">·</span>
                <span>cue #{activeCue?.cue_number ?? '—'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="pt-lcd pt-lcd--green text-[10px]">
                  {activeCue ? (activeCue.out_sec - activeCue.in_sec).toFixed(2) : '0.00'}s
                </span>
              </div>
            </div>
            <div className="relative flex-1 bg-pt-wave-bg p-1">
              <WaveformTimeline
                cueDurationSec={activeCue ? activeCue.out_sec - activeCue.in_sec : 0}
                refPeaks={activeCue?.ref_waveform?.peaks ?? null}
                takePeaks={null}
                cueInDisplay={cueIn}
                cueOutDisplay={cueOut}
              />
            </div>
            {/* Take history strip — PT chrome footer. */}
            <div className="border-t border-pt-border bg-pt-chrome px-3 py-2">
              <div className="mb-1 flex items-center justify-between pt-mono text-[10px] uppercase tracking-[0.16em] text-pt-text">
                <span className="font-bold">TAKES</span>
                {localTakeCount > 0 && (
                  <span className="tabular-nums text-pt-text-soft">
                    {localTakeCount} this session
                  </span>
                )}
              </div>
              <TakeHistory cueId={activeCueId} onPlay={() => undefined} />
            </div>
          </section>

          {/* Supervisor pickup banner (if applicable) */}
          {activeCue && pickupQueue.has(activeCue.cue_id) && (
            <SupervisorBanner
              supervisorName={activeCue.pickup_supervisor_name}
              note={activeCue.pickup_note}
              locale={locale}
            />
          )}

          {/* Predictive hint or reset reminder (one at a time) */}
          {resetReminderText ? (
            <ResetReminder
              text={resetReminderText}
              locale={locale}
              onDismiss={() => setResetReminder(null)}
            />
          ) : (
            <PredictiveHint category={predictiveCategory} locale={locale} />
          )}

          {/* Current line — PT panel with black accent rule + paper bg. */}
          <div className="relative rounded-sm border border-pt-border bg-pt-wave-bg px-5 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
            <span
              aria-hidden="true"
              className="absolute left-0 top-2 bottom-2 w-[2px] bg-pt-text"
            />
            <div className="text-center">
              <div className="text-[17px] font-medium leading-relaxed text-pt-text">
                {activeCue ? `“${activeCue.line_text}”` : '—'}
              </div>
              {activeCue && (
                <div className="mt-1.5 flex items-center justify-center gap-2 pt-mono text-[10px] uppercase tracking-[0.16em] text-pt-text-soft">
                  <span>cue #{activeCue.cue_number}</span>
                  <span>·</span>
                  <span className="tabular-nums">
                    {(activeCue.out_sec - activeCue.in_sec).toFixed(2)}s
                  </span>
                  <span>·</span>
                  <span className="text-pt-wave font-bold">{activeCue.category}</span>
                </div>
              )}
            </div>
          </div>

          {/* Big record button + secondary actions — PT transport row. */}
          <div className="flex flex-col items-center gap-3 py-1">
            <BigRecordButton
              phase={phase}
              disabled={!activeCue || !network.online || !recorder.isReady}
              loadingLabel={!recorder.isReady ? 'Loading recorder…' : null}
              onClick={() => {
                if (phase === 'recording') {
                  machine.abort()
                  return
                }
                if (phase === 'review') {
                  requestAdvance()
                  return
                }
                void machine.beginRecord()
              }}
              locale={locale}
            />
            <div className="flex flex-wrap items-start justify-center gap-2">
              <SecondaryButtons
                phase={phase}
                hasLastTake={hasLocalTake}
                onPreview={onPreview}
                onPlayLast={onPlayLast}
                onPickup={() => void machine.pickup()}
                onNext={requestAdvance}
              />
              {/* DAW-recorded WAV upload — same take chain as browser recording. */}
              <UploadTakeButton
                cueId={activeCueId}
                disabled={
                  phase === 'recording' ||
                  phase === 'three_pop' ||
                  phase === 'qc_local' ||
                  phase === 'upload' ||
                  phase === 'qc_server'
                }
              />
            </div>
          </div>

          {/* Progress bar — PT chrome footer. */}
          <div className="mt-auto rounded-sm border border-pt-border bg-pt-chrome px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]">
            <div className="mb-1 flex items-center justify-between pt-mono text-[10px] uppercase tracking-[0.16em] text-pt-text">
              <span className="font-bold">BATCH PROGRESS</span>
              <span className="tabular-nums">
                <span className="font-bold">{approvedCount.toString().padStart(3, '0')}</span>
                <span className="text-pt-text-soft">
                  {' '}
                  / {myCues.length.toString().padStart(3, '0')}
                </span>
                <span className="ml-2 pt-lcd pt-lcd--green text-[10px]">
                  {progressPct.toFixed(0)}%
                </span>
              </span>
            </div>
            <ProgressBar value={progressPct} />
            {lastError && (
              <p className="pt-ribbon mt-2 px-3 py-1.5 text-[12px]" role="alert">
                {lastError}
              </p>
            )}
          </div>

          {/* Inline horizontal fader for sub-lg breakpoints. */}
          <div className="border-t border-pt-border pt-2 lg:hidden">
            <RefVoiceFader orientation="horizontal" />
          </div>
        </main>

        {/* RIGHT — script panel (lg+ only). */}
        <aside className="hidden h-full border-l border-pt-border lg:flex lg:flex-col">
          <div className="border-b border-pt-border bg-pt-chrome px-3 py-2">
            <BlockModeButtons
              sceneCueCount={myCues.length}
              sceneLabel={`Scene ${activeCue?.scene_number ?? '—'}`}
              active={blockMode}
              onChoose={(m) => setBlockMode(m)}
              rangeAvailable={false}
              locale={locale}
            />
          </div>
          <div className="min-h-0 flex-1">
            <ScriptPanel
              scenes={data?.scenes ?? []}
              cues={cues}
              activeCueId={activeCue?.cue_id ?? null}
              onJumpToCue={(id) => setCurrentCue(id)}
              onRecordFullScene={onSceneBlock}
              onCustomRange={onCustomRange}
              pickupQueue={pickupQueue}
              locale={locale}
            />
          </div>
        </aside>
      </div>

      <VariancePrompt
        open={variancePromptOpen}
        variance={lengthVariance}
        onKeep={dismissVariancePrompt}
        onRetake={() => {
          dismissVariancePrompt()
          void machine.pickup()
        }}
        onMarkForReview={dismissVariancePrompt}
        onClose={dismissVariancePrompt}
      />

      <ThreeTakePrompt
        open={threeTakePromptOpen}
        takeCount={localTakeCount}
        onPickBest={dismissThreeTakePrompt}
        onRecordFourth={() => {
          dismissThreeTakePrompt()
          void machine.beginRecord()
        }}
        onSkipCue={() => {
          dismissThreeTakePrompt()
          machine.advance()
        }}
        onClose={dismissThreeTakePrompt}
      />

      <Drawer
        open={settingsDrawerOpen}
        onClose={toggleSettingsDrawer}
        side="right"
        title="Settings"
      >
        <div className="space-y-4 p-2">
          <section>
            <h3 className="mb-2 pt-mono text-[10px] font-bold uppercase tracking-[0.18em] text-pt-text">
              Mixer
            </h3>
            <MiniMixer />
          </section>
          <section>
            <h3 className="mb-2 pt-mono text-[10px] font-bold uppercase tracking-[0.18em] text-pt-text">
              Take history
            </h3>
            <TakeHistory cueId={activeCue?.cue_id ?? null} onPlay={() => undefined} />
          </section>
        </div>
      </Drawer>
    </>
  )
}
